/**
 * Scrolling an element into view without disturbing anything above it.
 *
 * `scrollIntoView` scrolls *every* scrollable ancestor -- including the
 * document once anything portalled into `<body>` has left the page scrollable,
 * which scrolls a whole page away under a `position: fixed` chrome. These
 * helpers move one box and stop.
 */

/** Nearest ancestor that both allows scrolling and currently has room to. */
export const findScrollPort = (element: HTMLElement): HTMLElement | null => {
  let candidate = element.parentElement;
  while (candidate) {
    const { overflowY } = window.getComputedStyle(candidate);
    if (
      (overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflowY === 'overlay') &&
      candidate.scrollHeight > candidate.clientHeight
    ) {
      return candidate;
    }
    candidate = candidate.parentElement;
  }
  return null;
};

/**
 * Center `element` in its own scroll box, touching nothing outside it.
 * `topInset` is the height pinned over that box; centering against the whole
 * box would leave high targets hidden behind it.
 */
export const centerInScrollPort = (element: HTMLElement, topInset = 0) => {
  const port = findScrollPort(element);
  if (!port) return;
  const entry = element.getBoundingClientRect();
  const view = port.getBoundingClientRect();
  const usableTop = view.top + topInset;
  const usableHeight = Math.max(view.height - topInset, 0);
  // Too tall to center below the panel without its top going behind it.
  const delta =
    entry.height >= usableHeight
      ? entry.top - usableTop
      : entry.top + entry.height / 2 - (usableTop + usableHeight / 2);
  port.scrollTo({ top: port.scrollTop + delta, behavior: 'smooth' });
};
